/**
 * Exportação em CSV do que está na tela.
 *
 * Duas decisões que parecem detalhe e não são, porque quem abre este arquivo
 * abre no Excel em português:
 *
 * **Separador `;`.** O Excel em pt-BR lê `,` como separador decimal, não de
 * coluna. Com vírgula, "1,5" vira duas células e a planilha inteira sai
 * torta — sem nenhuma mensagem de erro, que é o pior jeito de errar.
 *
 * **BOM UTF-8 no começo.** Sem ele o Excel assume a página de código local e
 * "Resolução" vira "ResoluÃ§Ã£o". O BOM é invisível em qualquer outro leitor.
 *
 * Os números já chegam formatados em pt-BR pelas telas (vírgula decimal), o
 * que combina com o separador escolhido.
 */

export interface ColunaDeCsv<L> {
  cabecalho: string;
  valor: (linha: L) => string | number | null | undefined;
}

const SEPARADOR = ';';
const BOM = '﻿';

/**
 * Escapa um campo.
 *
 * Aspas duplicadas e o campo entre aspas quando contém separador, aspas ou
 * quebra de linha — a regra do RFC 4180. O `\r` também entra na conta: um
 * assunto de chamado colado do Outlook costuma trazer `\r\n`.
 */
export function escaparCampo(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  const texto = String(valor);
  if (!/[";\n\r]/.test(texto)) return texto;
  return `"${texto.replace(/"/g, '""')}"`;
}

export function paraCsv<L>(colunas: ReadonlyArray<ColunaDeCsv<L>>, linhas: readonly L[]): string {
  const cabecalho = colunas.map((coluna) => escaparCampo(coluna.cabecalho)).join(SEPARADOR);
  const corpo = linhas.map((linha) =>
    colunas.map((coluna) => escaparCampo(coluna.valor(linha))).join(SEPARADOR),
  );
  // `\r\n`: é o que o RFC 4180 pede, e o que o Excel espera.
  return `${BOM}${[cabecalho, ...corpo].join('\r\n')}\r\n`;
}

/** Nome de arquivo previsível: `relatorio-volume-por-equipe-2026-08-23.csv`. */
export function nomeDeArquivo(partes: readonly string[], em = new Date()): string {
  const pedaco = partes
    .join('-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${pedaco}-${em.toISOString().slice(0, 10)}.csv`;
}

/**
 * Entrega o arquivo ao navegador.
 *
 * Âncora com `download` e `blob:` em vez de rota no servidor: o dado já está
 * na tela, e ir buscá-lo de novo abriria a porta para o arquivo baixado não
 * ser o que a pessoa está vendo — recorte diferente, minuto diferente,
 * discussão na reunião seguinte.
 */
export function baixarCsv(nome: string, conteudo: string): void {
  const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  document.body.append(ancora);
  ancora.click();
  ancora.remove();
  // Sem o revoke, o blob fica preso na memória da aba até ela ser fechada.
  URL.revokeObjectURL(url);
}
